import React from 'react';
import { LuxuryCard } from './LuxuryCard';
import { LuxuryItem } from '../types';
import { Bitcoin, Gem, Crown } from 'lucide-react';

interface LuxuryGridProps {
  items: LuxuryItem[];
  bitcoinPrice: number;
}

interface SectionHeaderProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, description, icon }) => (
  <div className="col-span-full mb-8">
    <div className="flex items-center gap-3 mb-2">
      {icon}
      <h2 className="text-3xl font-bold text-gray-900">
        {title}
      </h2>
    </div>
    <p className="text-gray-600">{description}</p>
  </div>
);

export const LuxuryGrid: React.FC<LuxuryGridProps> = ({ items, bitcoinPrice }) => {
  // Split items into sections based on price
  const surviveItems = items.filter(item => item.price <= 500000);
  const thriveItems = items.filter(item => item.price > 500000 && item.price <= 10000000);
  const flourishItems = items.filter(item => item.price > 10000000);

  return (
    <div className="space-y-16">
      {/* Survive Section */}
      <section className="relative">
        <SectionHeader
          title="Survive"
          description="Basic luxury items for the successful professional ($1k - $500k)"
          icon={<Bitcoin className="w-8 h-8 text-orange-500" />}
        />
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {surviveItems.map((item) => (
            <LuxuryCard
              key={item.name}
              item={item}
              bitcoinPrice={bitcoinPrice}
            />
          ))}
        </div>
      </section>

      {/* Thrive Section */}
      <section className="relative">
        <SectionHeader
          title="Thrive"
          description="Premium luxury items for high-net-worth individuals ($500k - $10M)"
          icon={<Gem className="w-8 h-8 text-orange-500" />}
        />
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {thriveItems.map((item) => (
            <LuxuryCard
              key={item.name}
              item={item}
              bitcoinPrice={bitcoinPrice}
            />
          ))}
        </div>
      </section>

      {/* Flourish Section */}
      <section className="relative">
        <SectionHeader
          title="Flourish"
          description="Ultra luxury items for the ultra-wealthy ($10M+)"
          icon={<Crown className="w-8 h-8 text-orange-500" />}
        />
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {flourishItems.map((item) => (
            <LuxuryCard
              key={item.name}
              item={item}
              bitcoinPrice={bitcoinPrice}
            />
          ))}
        </div>
      </section>
    </div>
  );
};